// A6 Field Green token source of truth. All A6 hex lives in theme/ (the ratchet
// excludes this dir). Light chrome values mirror website/tokens/a6-tokens.ts;
// dark values come from the design spec §3.3. Consumed only by theme.ts.
// Keep in lock-step with the website seed — theme/tokens.guard.test.ts enforces it.

export const grayRamp = {
  50: '#F5F4EF', 100: '#EDEBE3', 200: '#E4E2D8', 300: '#D2D0C4', 400: '#9A9A8D',
  500: '#6A6D62', 600: '#565A4F', 700: '#3B4038', 800: '#282C25', 900: '#1B1E19', 950: '#121411',
} as const

// Dark counterpart of grayRamp. Tonally reflected + green-tinted to match the
// chrome dark ladder (canvas #0F1210 … ink #EDEEEA). Low steps read as dark
// surfaces/borders, high steps as light ink — so bg="gray.100" flips to a dark
// surface and color="gray.700" flips to light text, both with zero call-site edits.
// Slice 7 makes gray mode-aware; the named-token migration is slice 8.
export const grayRampDark = {
  50: '#0F1210', 100: '#141814', 200: '#1E231F', 300: '#2B312B', 400: '#6B716A',
  500: '#8A8F87', 600: '#A3A89F', 700: '#C3C7C0', 800: '#D9DCD5', 900: '#EDEEEA', 950: '#F4F5F1',
} as const

// Chakra's stock blue ramp, copied verbatim so the mode-aware light values are
// byte-identical to today. Only added so blue can be made conditional in theme.ts.
export const blueRamp = {
  50: '#eff6ff', 100: '#dbeafe', 200: '#bfdbfe', 300: '#a3cfff', 400: '#60a5fa', 500: '#3b82f6',
  600: '#2563eb', 700: '#173da6', 800: '#1a3478', 900: '#14204a', 950: '#0c142e',
} as const

// Dark counterpart. Reflected so pale-bg roles (blue.50) flip to dark selected
// backgrounds and foreground roles (blue.400/500 spinners, party-lean fills) stay
// vivid and readable on the dark canvas. blue.500 dark == the info token's dark fg,
// so party-blue and info-blue agree in dark. Covers all 217 raw blue.NN sites; the
// chrome-blue -> Field-Green identity migration is deferred to slice 8 (party-blue stays).
export const blueRampDark = {
  50: '#0c142e', 100: '#14204a', 200: '#1a3478', 300: '#22468f', 400: '#5a9bf0', 500: '#60a5fa',
  600: '#7db4fb', 700: '#a3cfff', 800: '#c7e0ff', 900: '#e0edff', 950: '#eff6ff',
} as const

export const brandRamp = {
  50: '#E7F1EB', 100: '#C6DFCF', 200: '#9FCAB1', 300: '#6DAF8A', 400: '#3C8E63',
  500: '#17603C', 600: '#124F30', 700: '#0E4028', 800: '#0B3220', 900: '#082517', 950: '#05180F',
} as const

export const radiiScale = { xs: '6px', sm: '8px', md: '10px', lg: '12px', xl: '14px' } as const

// DT-5. Chakra v3 dropped v2's sizes.container.* scale, so every in-tree
// maxW="container.*" emitted an INVALID max-width that the browser drops — which also
// defeated the v3 Container recipe's own `maxWidth: "8xl"` (=90rem=1440px) default,
// leaving those elements uncapped (LoginPage spanned the monitor). Re-declare the scale.
// sm/md/lg restore the v2 values the 41 call sites were authored against. `xl` is 1440,
// NOT v2's 1280: 1280 would narrow every dashboard page by 160px through PageContainer's
// default and reverse A6 §4.3. `2xl` is clamped to the same cap (v2's 1536 exceeds it);
// nothing uses it today — it is declared so a future use can't silently go uncapped again.
export const containerSizes = {
  sm: '640px',
  md: '768px',
  lg: '1024px',
  xl: '1440px',
  '2xl': '1440px',
} as const

export const chrome = {
  canvas:       { light: '#F5F4EF', dark: '#0F1210' },
  canvas2:      { light: '#EEEDE5', dark: '#141814' },
  surface:      { light: '#FCFBF8', dark: '#171B18' },
  surface2:     { light: '#F7F6F0', dark: '#1C211D' },
  ink:          { light: '#1B1E19', dark: '#EDEEEA' },
  ink2:         { light: '#3B4038', dark: '#C3C7C0' },
  muted:        { light: '#6A6D62', dark: '#8A8F87' },
  border:       { light: '#E4E2D8', dark: '#262C28' },
  border2:      { light: '#EEEDE6', dark: '#1F2521' },
  accent:       { light: '#17603C', dark: '#2F9B63' },
  accentStrong: { light: '#124F30', dark: '#3AAF72' },
  accentSoft:   { light: 'rgba(23,96,60,.09)', dark: 'rgba(47,155,99,.16)' },
  onAccent:     { light: '#FFFFFF', dark: '#08130D' },
  band:         { light: '#16281F', dark: '#1D3528' }, // dark reads as a band above canvas #0F1210
  onBand:       { light: '#FFFFFF', dark: '#FFFFFF' }, // band stays dark in both modes -> fg stays light in both
} as const

export const status = {
  success: { fg: { light: '#047857', dark: '#34D399' }, tint: { light: '#DFF3EA', dark: 'rgba(52,211,153,.14)' } },
  warning: { fg: { light: '#B45309', dark: '#FBBF24' }, tint: { light: '#FBF0DF', dark: 'rgba(251,191,36,.14)' } },
  danger:  { fg: { light: '#B42318', dark: '#F87171' }, tint: { light: '#FBE9E7', dark: 'rgba(248,113,113,.14)' } },
  info:    { fg: { light: '#1D4ED8', dark: '#60A5FA' }, tint: { light: '#E6ECFC', dark: 'rgba(96,165,250,.14)' } },
} as const

export const fontFamilies = {
  sans: '"Geist","Segoe UI Variable Text","SF Pro Text",-apple-system,BlinkMacSystemFont,"Segoe UI",system-ui,sans-serif',
  mono: '"Geist Mono","SF Mono","Cascadia Code",ui-monospace,"Roboto Mono",monospace',
} as const

// A6 §3.4: exactly two shadows, both for elements that float with no surface
// ladder beneath them. Green-tinted black (ink = #1B1E19 = rgb(27,30,25)) so the
// elevation reads warm, not neutral-gray. popover = dropdowns/popovers/tooltips/
// bottom sheets; modal = dialogs + the analytics-map floating toolbar. Single
// value for now (light-mode only app); slice 7 refines dark like the band token.
export const shadows = {
  popover: {
    light: '0 4px 12px rgba(27,30,25,0.10), 0 1px 3px rgba(27,30,25,0.07)',
    dark:  '0 4px 14px rgba(0,0,0,0.44), 0 1px 3px rgba(0,0,0,0.34)',
  },
  modal: {
    light: '0 14px 36px rgba(27,30,25,0.18), 0 3px 10px rgba(27,30,25,0.10)',
    dark:  '0 16px 40px rgba(0,0,0,0.55), 0 4px 12px rgba(0,0,0,0.40)',
  },
} as const

// --- A6 slice 9: data-viz palette (dataviz skill). Separate from Field-Green
// chrome by design (spec §3.1). Categorical = dataviz default, validated in both
// modes against surface #FCFBF8/#171B18. Off-map only; the constant map palette
// lives in components/analyticsMap/viz/mapRamps.ts. ---
export const vizCat = {
  1: '#2a78d6', 2: '#eb6834', 3: '#1baf7a', 4: '#eda100',
  5: '#e87ba4', 6: '#008300', 7: '#4a3aa7', 8: '#e34948',
} as const
export const vizCatDark = {
  1: '#3987e5', 2: '#d95926', 3: '#199e70', 4: '#c98500',
  5: '#d55181', 6: '#008300', 7: '#9085e9', 8: '#e66767',
} as const

// Sequential blue, light->dark. Dark = same hexes reversed so near-zero recedes
// toward the dark surface ("flips anchor in dark").
export const vizSeq = {
  100: '#cde2fb', 200: '#9ec5f4', 300: '#6da7ec', 400: '#3987e5',
  500: '#256abf', 600: '#184f95', 700: '#0d366b',
} as const
export const vizSeqDark = {
  100: '#0d366b', 200: '#184f95', 300: '#256abf', 400: '#3987e5',
  500: '#6da7ec', 600: '#9ec5f4', 700: '#cde2fb',
} as const

// Diverging blue<->red with a neutral gray midpoint. Partisan lean/margin idiom.
export const vizDiv = { neg: '#e34948', mid: '#f0efec', pos: '#2a78d6' } as const
export const vizDivDark = { neg: '#e66767', mid: '#6d6d67', pos: '#3987e5' } as const

// Domain party mapping (fixed by meaning; consistent with the diverging poles).
export const vizParty = {
  democrat: '#2a78d6', republican: '#e34948', independent: '#4a3aa7',
  libertarian: '#eda100', green: '#008300', other: '#8A8F87',
} as const
export const vizPartyDark = {
  democrat: '#3987e5', republican: '#e66767', independent: '#9085e9',
  libertarian: '#c98500', green: '#008300', other: '#8A8F87',
} as const
